// `import type` é apagado na compilação — não cria dependência circular em
// runtime, mesmo `config-migration.ts` importando tipos deste arquivo.
import type { RunForestNode } from "@/api/config-migration";

/** Um nó real da estrutura organizacional, como a API devolve. */
export type OrgNameStatus = "extracted" | "not_extracted";
export type OrgRelationStatus =
  | "assigned"
  | "no_parent"
  | "unknown_parent"
  | "many_to_many"
  | "not_applicable";
export type OrgNameSource =
  | "direct_field"
  | "text_table"
  | "not_available"
  /** Nome que o consultor corrigiu — não veio da extração (#845). */
  | "consultant_override";
export type OrgConformityStatus = "conforming" | "non_conforming" | "not_evaluable";
/** Procedência da unidade: extraída do SAP ou criada à mão no gerenciamento (#845). */
export type UnitOrigin = "sap" | "manual";
/** Veredito de escopo da unidade — decisão de gerenciamento, não de execução. */
export type ScopeVerdict = "pending" | "approved" | "out_of_scope";

/** Associação N:M declarada pela origem — ex.: um Shipping Point ligado a vários Plants. */
export interface OrgAssociation {
  level_code: string;
  id: string;
}

export interface OrgConformity {
  status: OrgConformityStatus;
  rule: string;
  reason: string;
}

export interface OrgStructureNode {
  id: string;
  name: string | null;
  parent_id: string | null;
  status: string;
  country?: string | null;
  city?: string | null;
  local_currency?: string | null;
  /** Subtipo do CBC (storage_location/shipping_point/purchasing_organization) —
   *  da sobreposição (nó extraído) ou de `attributes.subtype` (nó manual). */
  subtype?: string | null;
  name_status?: OrgNameStatus;
  relation_status?: OrgRelationStatus;
  name_source?: OrgNameSource;
  /**
   * Chave natural desta unidade em `config_migration_item`, calculada pelo
   * SERVIDOR. É a mesma que o plano de execução e o veredito de escopo usam:
   * derivar a chave no cliente já custou um bug em que aprovar não habilitava
   * executar (veredito gravava o id simples, o gate consultava a composta).
   */
  item_key?: string;
  conformity: OrgConformity;
  associations?: OrgAssociation[];
  warehouse_number?: string | null;
  // Gerenciamento (#845)
  origin?: UnitOrigin;
  scope_verdict?: ScopeVerdict;
  overridden_fields?: string[];
  /** Valor que a extração trouxe para cada campo de `overridden_fields`. */
  original_values?: Record<string, string | null>;
  /** Id corrigido pelo consultor — o id que a unidade terá no CBC. `id`
   *  continua o extraído, porque é por ele que os filhos se ligam ao pai. */
  target_id?: string | null;
  migration_item_id?: string | null;
}

/** Um nível do cookbook com os nós que a extração encontrou nele. */
export interface OrgStructureLevel {
  level_code: string;
  level_name: string;
  parent_level_code: string | null;
  sequence: number;
  source_table: string | null;
  naming_rule: string | null;
  maintainable_in_org_app: boolean;
  extraction_status: string;
  node_count: number;
  nodes: OrgStructureNode[];
}

/**
 * Por que um nó virou raiz sem que seu nível seja raiz:
 * - `no_parent`: o campo de pai veio vazio na origem (legítimo no SAP — 54 dos
 *   164 company codes do ECC real não têm Company atribuída);
 * - `unknown_parent`: o campo aponta para um pai que a extração não trouxe.
 */
export type OrphanReason = null | "no_parent" | "unknown_parent";

/** Status de cadastro no CBC — casado ao nó via `${item_type}:${item_key}`. */
export type MigrationStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "interrupted"
  | "awaiting_human_decision";

/**
 * Decisão humana pendente numa unidade (§5.8 do design) — hoje só existe o
 * `kind: "id_rejected"` (SAP recusou o ID puramente numérico), mas o formato
 * já comporta texto livre ou select sem mudança de schema.
 */
export interface PendingDecision {
  kind: string;
  message: string;
  suggestion?: string;
  decided?: string;
  /** O ID digitado no terminal, à espera do "sim" que o confirma. */
  proposed?: string;
}

/**
 * Um item de migração, como o endpoint de items da Task 12 devolve.
 *
 * `id` e `pending_decision` são opcionais aqui só para não quebrar as
 * fixtures de teste já existentes (Tasks 12/17), que montam objetos
 * `MigrationItem` à mão sem os dois campos — a API real sempre os envia.
 */
export interface MigrationItem {
  id?: string;
  item_type: string;
  item_key: string;
  status: MigrationStatus;
  outcome: string | null;
  external_id: string | null;
  attempts: number;
  last_run_id: string | null;
  pending_decision?: PendingDecision | null;
}

export interface OrgTreeNode {
  key: string;
  levelCode: string;
  levelName: string;
  unitTypeKey: string;
  unitTypeLabel: string;
  id: string;
  name: string | null;
  status: string;
  country: string | null;
  city: string | null;
  localCurrency: string | null;
  /** Subtipo do CBC (storage_location/shipping_point/purchasing_organization),
   *  ou `null` para tipo sem subtype. */
  subtype: string | null;
  migrationStatus: MigrationStatus;
  nameStatus: OrgNameStatus;
  relationStatus: OrgRelationStatus;
  nameSource: OrgNameSource;
  conformity: OrgConformity;
  orphanReason: OrphanReason;
  parent: OrgTreeNodeSummary | null;
  /**
   * `parent_id` cru da extração, antes da resolução de pai. Distinto de
   * `parent`, que é `null` quando o pai não veio na extração
   * (`unknown_parent`): a chave de identidade tem que sair do dado bruto,
   * senão o nó órfão teria uma chave no casamento do status e outra na
   * floresta enviada ao backend.
   */
  parentId: string | null;
  associations?: OrgAssociation[];
  warehouseNumber?: string | null;
  /** `MigrationItem.last_run_id` projetado — a tela de Execução liga o terminal a este run. */
  lastRunId: string | null;
  /** `MigrationItem.pending_decision` projetado. */
  pendingDecision: PendingDecision | null;
  /**
   * `MigrationItem.id` projetado — o id de banco do item de migração, que a exclusão
   * da unidade manual usa (`/org-structure/units/{item_id}`). Não é o
   * `id` da unidade SAP (esse já está em `node.id`): são valores diferentes,
   * e confundi-los quebraria a chamada em runtime sem nenhum teste local
   * acusar, porque o fake de teste não teria como distinguir os dois.
   */
  migrationItemId: string | null;
  /**
   * Chave natural que o SERVIDOR calculou (ver `OrgStructureNode.item_key`).
   * Toda escrita que identifica a unidade — veredito, sobreposição, plano de
   * execução — usa esta, nunca uma chave derivada no cliente.
   */
  itemKey: string;
  /** Procedência: extraída do SAP ou criada à mão no gerenciamento (#845). */
  origin: UnitOrigin;
  /** Veredito de escopo da unidade — decisão de gerenciamento, independente de execução. */
  scopeVerdict: ScopeVerdict;
  /** Campos de atributo sobrescritos manualmente em relação ao valor extraído. */
  overriddenFields: string[];
  /** Valor extraído de cada campo corrigido — o que a tela mostra riscado. */
  originalValues: Record<string, string | null>;
  /**
   * Id corrigido pelo consultor (unidade da extração). Exibir e cadastrar
   * usam `effectiveOrgId`; ligar filho a pai e montar chave usam `id`, que
   * segue sendo o extraído.
   */
  targetId: string | null;
  /** Cópia de uma unidade muitos-para-muitos sob um dos pais dela (a mesma unidade, ``itemKey`` igual). */
  linkedCopy?: boolean;
  children: OrgTreeNode[];
}

export interface OrgTreeNodeSummary {
  key: string;
  levelCode: string;
  levelName: string;
  unitTypeLabel?: string;
  id: string;
  targetId?: string | null;
  name: string | null;
}

export interface OrgForest {
  roots: OrgTreeNode[];
  emptyLevels: OrgStructureLevel[];
}

/**
 * Chave de BUSCA de pai: identifica um nó dentro do seu nível.
 *
 * Não serve como key de render: em níveis cuja chave SAP só é única dentro do
 * pai (Storage Location — dois plants podem ter S001 cada), dois nós diferentes
 * produzem o mesmo valor aqui. Para o lado do pai isso não é problema: os
 * níveis que são pai de alguém (Company, Company Code, Plant) têm chave única
 * no cliente.
 */
function lookupKey(levelCode: string, id: string): string {
  return `${levelCode}:${id}`;
}

/** Chave de RENDER: única mesmo em nível de chave composta. */
function renderKey(levelCode: string, id: string, parentId: string | null): string {
  return `${levelCode}:${parentId ?? "-"}:${id}`;
}

// Pai criado dentro do formulário do filho (o `created_inline_in` do catálogo do
// CBC): a company nasce no "Create New Company" do company code, sem cadastro antes.
const PARENT_CREATED_IN_CHILD_FORM = new Set(["company_code:company"]);

/**
 * Regra única do bloqueio "pai ainda não cadastrado": a unidade tem pai e esse pai
 * não está `completed` (ou não veio na floresta consultada). Vale para o ▶ da linha
 * e para o terminal da Execução, que mostram o mesmo motivo.
 */
export function isOrgParentNotRegistered(
  node: Pick<OrgTreeNode, "parent" | "levelCode">,
  findNode: (key: string) => Pick<OrgTreeNode, "migrationStatus"> | null | undefined,
): boolean {
  const { parent } = node;
  if (!parent) return false;
  if (PARENT_CREATED_IN_CHILD_FORM.has(`${node.levelCode}:${parent.levelCode}`)) return false;
  return findNode(parent.key)?.migrationStatus !== "completed";
}

/**
 * A Company não tem cadastro próprio no CBC: nasce no formulário do Company Code. O
 * estado dela na Execução é o dos Company Codes que a criam: cadastrada quando algum
 * deles foi, rodando quando algum roda. Os demais níveis usam o estado do próprio nó.
 */
export function executionMigrationStatus(
  node: Pick<OrgTreeNode, "levelCode" | "migrationStatus" | "children">,
): MigrationStatus {
  if (node.levelCode !== "company") return node.migrationStatus;
  const codes = node.children.filter((child) => child.levelCode === "company_code");
  if (codes.some((code) => code.migrationStatus === "completed")) return "completed";
  if (codes.some((code) => code.migrationStatus === "running")) return "running";
  return node.migrationStatus;
}

// Unidades que o formulário de outra cadastra junto: a Company no do Company Code, o
// Distribution Channel e a Division no da Sales Area. Não abrem conversa própria no "Run all".
const UNITS_CREATED_INSIDE_ANOTHER: ReadonlySet<string> = new Set([
  "company",
  "distribution_channel",
  "division",
]);

// Fora da fila do "Run all": já cadastrada, em cadastro agora ou esperando resposta do
// consultor. Falhou e interrompida entram (a fila tenta de novo).
const QUEUE_SKIPPED_STATUSES: ReadonlySet<MigrationStatus> = new Set([
  "completed",
  "running",
  "awaiting_human_decision",
]);

/**
 * As unidades que o "Run all" abre, na ordem recebida (pai antes de filho). Sai a que a
 * Execução já mostra cadastrada (`executionMigrationStatus`: a Company vale o que o
 * Company Code dela fez, e o item dela fica `pending` no banco) e a que o motor não
 * abre sozinha (a Company nasce no formulário do Company Code; o Distribution Channel e a
 * Division, no da Sales Area). Sai também a unidade
 * cujo pai não está cadastrado nem vai na fila: ela só falharia. A contagem do diálogo
 * e o disparo usam esta mesma função.
 */
export function selectOrgRunQueue(
  nodes: OrgTreeNode[],
  all: ReadonlyMap<string, OrgTreeNode>,
): OrgTreeNode[] {
  const queued = new Set<string>();
  const queue: OrgTreeNode[] = [];
  for (const node of nodes) {
    if (queued.has(node.key) || node.scopeVerdict !== "approved") continue;
    if (UNITS_CREATED_INSIDE_ANOTHER.has(node.levelCode)) continue;
    if (QUEUE_SKIPPED_STATUSES.has(executionMigrationStatus(node))) continue;
    const parentBlocked = isOrgParentNotRegistered(node, (key) =>
      queued.has(key) ? { migrationStatus: "completed" } : all.get(key),
    );
    if (parentBlocked) continue;
    queued.add(node.key);
    queue.push(node);
  }
  return queue;
}

/** O id que a unidade mostra e com que será cadastrada no CBC: o corrigido
 *  pelo consultor, quando houver, ou o extraído. */
export function effectiveOrgId(node: Pick<OrgTreeNode, "id" | "targetId">): string {
  return node.targetId ?? node.id;
}

/**
 * Valor extraído de um campo que o consultor corrigiu, para a tela riscar ao
 * lado do valor novo. `null` quando não há o que riscar: campo não corrigido,
 * ou unidade manual (não existe extração para comparar). O invólucro
 * distingue "não corrigido" de "corrigido, e a extração não tinha valor".
 */
export function extractedValueOf(
  node: Pick<OrgTreeNode, "origin" | "overriddenFields" | "originalValues">,
  field: string,
): { value: string | null } | null {
  if (node.origin !== "sap" || !node.overriddenFields.includes(field)) return null;
  if (!(field in node.originalValues)) return null;
  return { value: node.originalValues[field] ?? null };
}

/**
 * Chave de IDENTIDADE do item de migração — a mesma nos três lugares que a
 * usam: o casamento do status, a floresta enviada ao backend e o play por
 * unidade.
 *
 * Composta com o pai porque `id` sozinho não identifica um nó em nível de
 * chave composta: Storage Location `0001` sob Plant 1000 e sob Plant 2000 é
 * rotina em ECC, e casar só por id fazia as duas unidades compartilharem uma
 * linha de `config_migration_item`. O separador `|` é o que o COMMENT da
 * migration já prometia (`sales_area "3110|10|00"`).
 *
 * Não confundir com `unit.id`, que continua sendo o id puro: é ele que vai
 * digitado no formulário do CBC. Esta chave é só identidade de status.
 */
export function itemKeyOf(node: { id: string; parentId: string | null }): string {
  return node.parentId ? `${node.parentId}|${node.id}` : node.id;
}

function fallbackNameStatus(node: OrgStructureNode): OrgNameStatus {
  return node.name === null ? "not_extracted" : "extracted";
}

function fallbackNameSource(node: OrgStructureNode): OrgNameSource {
  return node.name === null ? "not_available" : "direct_field";
}

function requireConformity(node: OrgStructureNode): OrgConformity {
  if (node.conformity == null) {
    throw new Error("Org structure node is missing required conformity");
  }
  return node.conformity;
}

export type ShippingPointSubtype = "standard" | "customer_consignment" | "return";
export type OrgUnitTypeFilter = string;
export type OrgCountryFilter = string;

export function orgUnitTypeLabelKey(unitTypeLabel: string | null | undefined, levelName: string): string {
  return unitTypeLabel?.trim() ? unitTypeLabel : levelName;
}

function shippingPointUnitTypeLabel(subtype: ShippingPointSubtype): string {
  if (subtype === "standard") return "Shipping Point · Standard";
  if (subtype === "customer_consignment") return "Shipping Point · Customer consignment";
  return "Shipping Point · Return";
}

function shippingPointUnitTypeKey(subtype: ShippingPointSubtype): string {
  return `shipping_point:${subtype}`;
}

export function classifyShippingPoint(
  id: string,
  namingRule: string | null,
): ShippingPointSubtype | null {
  const normalizedRule = namingRule?.trim().toLowerCase() ?? "";
  if (!normalizedRule.startsWith("country-specific, ##n0 with n=1,2,3")) {
    return null;
  }

  const normalizedId = id.trim().toUpperCase();
  const match = normalizedId.match(/^([0-9]{2})(10|1C|1R)$/);
  if (match === null) return null;

  const [, prefix, suffix] = match;
  if (!["10", "13", "17", "18"].includes(prefix)) {
    return null;
  }

  if (suffix === "10") return "standard";
  if (suffix === "1C") return "customer_consignment";
  if (suffix === "1R") return "return";
  return null;
}

function deriveUnitType(level: OrgStructureLevel, node: OrgStructureNode): {
  key: string;
  label: string;
} {
  if (level.level_code !== "shipping_point") {
    return {
      key: level.level_code,
      label: level.level_name,
    };
  }

  const subtype = classifyShippingPoint(node.id, level.naming_rule);
  if (subtype === null) {
    return {
      key: level.level_code,
      label: level.level_name,
    };
  }

  return {
    key: shippingPointUnitTypeKey(subtype),
    label: shippingPointUnitTypeLabel(subtype),
  };
}

/**
 * Monta a floresta a partir da lista achatada de níveis.
 *
 * Um nó é raiz quando seu nível não tem pai declarado, quando o valor do pai
 * veio vazio, ou quando o pai apontado não existe entre os nós extraídos. Isso
 * é deliberado: nenhum nó desaparece da árvore por causa de um vínculo que a
 * extração não conseguiu resolver.
 *
 * `migrationItems` é opcional para não quebrar os chamadores existentes (a
 * tela do Nó 02 chama esta função em mais de um lugar). Quando ausente, todo
 * nó nasce com `migrationStatus: "pending"`.
 */
export function buildOrgStructureForest(
  levels: OrgStructureLevel[],
  migrationItems: MigrationItem[] = [],
): OrgForest {
  const emptyLevels = levels.filter((level) => level.nodes.length === 0);
  const populated = levels.filter((level) => level.nodes.length > 0);

  const parentLevelOf = new Map<string, string | null>(
    levels.map((level) => [level.level_code, level.parent_level_code]),
  );

  // Casa por (item_type, item_key), nunca só por id: níveis de chave
  // composta (Storage Location) repetem id entre pais diferentes — casar só
  // por id misturaria unidades distintas. Guarda o item inteiro (não só o
  // status) porque lastRunId, pendingDecision e migrationItemId vêm dele.
  const migrationItemByKey = new Map<string, MigrationItem>(
    migrationItems.map((item) => [`${item.item_type}:${item.item_key}`, item]),
  );
  const migrationItemById = new Map(migrationItems.map((item) => [item.id, item]));

  const byLookupKey = new Map<string, OrgTreeNode>();
  const entries: Array<{ node: OrgTreeNode; parentId: string | null }> = [];

  for (const level of populated) {
    for (const node of level.nodes) {
      const unitType = deriveUnitType(level, node);
      // `item_key` vem do servidor; `itemKeyOf` fica só como fallback para
      // resposta antiga que ainda não traz o campo.
      const itemKey = node.item_key ?? itemKeyOf({ id: node.id, parentId: node.parent_id });
      const migrationItem = migrationItemById.get(node.migration_item_id ?? "")
        ?? migrationItemByKey.get(`${level.level_code}:${itemKey}`);
      const treeNode: OrgTreeNode = {
        key: renderKey(level.level_code, node.id, node.parent_id),
        levelCode: level.level_code,
        levelName: level.level_name,
        unitTypeKey: unitType.key,
        unitTypeLabel: unitType.label,
        id: node.id,
        name: node.name,
        status: node.status,
        country: node.country ?? null,
        city: node.city ?? null,
        localCurrency: node.local_currency ?? null,
        subtype: node.subtype ?? null,
        migrationStatus: migrationItem?.status ?? "pending",
        lastRunId: migrationItem?.last_run_id ?? null,
        pendingDecision: migrationItem?.pending_decision ?? null,
        // Prefere o valor da própria árvore (Task 4 — cobre unidade só
        // decidida, nunca executada); só cai para o merge de MigrationItem
        // quando a árvore não tem o campo (compatibilidade com dado antigo).
        migrationItemId: node.migration_item_id ?? migrationItem?.id ?? null,
        itemKey,
        nameStatus: node.name_status ?? fallbackNameStatus(node),
        relationStatus: node.relation_status ?? "assigned",
        nameSource: node.name_source ?? fallbackNameSource(node),
        conformity: requireConformity(node),
        orphanReason: null,
        parent: null,
        parentId: node.parent_id,
        associations: node.associations,
        warehouseNumber: node.warehouse_number,
        // Lidos direto do nó — decisão de escopo não depende de execução.
        origin: node.origin ?? "sap",
        scopeVerdict: node.scope_verdict ?? "pending",
        overriddenFields: node.overridden_fields ?? [],
        originalValues: node.original_values ?? {},
        targetId: node.target_id ?? null,
        children: [],
      };
      const lookup = lookupKey(level.level_code, node.id);
      if (!byLookupKey.has(lookup)) {
        byLookupKey.set(lookup, treeNode);
      }
      entries.push({ node: treeNode, parentId: node.parent_id });
    }
  }

  const roots: OrgTreeNode[] = [];

  for (const { node, parentId } of entries) {
    const parentLevel = parentLevelOf.get(node.levelCode) ?? null;

    if (parentLevel === null) {
      if (node.relationStatus === "assigned") {
        node.relationStatus = "not_applicable";
      }
      roots.push(node);
      continue;
    }
    if (parentId === null) {
      // Muitos-para-muitos é o modelo correto para esse nó (ex.: Shipping
      // Point ligado a vários Plants) — a ausência de pai único aqui não é
      // uma falha de vínculo, então o nó vira raiz sem orphanReason.
      if (node.relationStatus === "many_to_many") {
        // Como no Diagnóstico, a unidade aparece sob CADA pai a que está ligada
        // (``associations``). A árvore tem um pai por nó, então cada pai recebe uma
        // CÓPIA com chave própria; as cópias dizem a mesma unidade (mesmo ``itemKey``) e a
        // contagem conta uma vez só (``uniqueOrgUnits``). Sem pai na árvore, vira raiz.
        const parents = (node.associations ?? [])
          .map((association) => byLookupKey.get(lookupKey(association.level_code, association.id)))
          .filter((parent): parent is OrgTreeNode => parent !== undefined);
        if (parents.length === 0) {
          roots.push(node);
        } else {
          for (const parent of parents) {
            parent.children.push({
              ...node,
              key: `${node.key}@${parent.key}`,
              linkedCopy: true,
              children: [],
            });
          }
        }
        continue;
      }
      node.relationStatus = node.relationStatus === "assigned" ? "no_parent" : node.relationStatus;
      node.orphanReason = "no_parent";
      roots.push(node);
      continue;
    }
    const parent = byLookupKey.get(lookupKey(parentLevel, parentId));
    if (parent === undefined) {
      node.relationStatus =
        node.relationStatus === "assigned" ? "unknown_parent" : node.relationStatus;
      node.orphanReason = "unknown_parent";
      roots.push(node);
      continue;
    }
    node.relationStatus = node.relationStatus === "not_applicable" ? "assigned" : node.relationStatus;
    node.parent = {
      key: parent.key,
      levelCode: parent.levelCode,
      levelName: parent.levelName,
      unitTypeLabel: parent.unitTypeLabel,
      id: parent.id,
      targetId: parent.targetId,
      name: parent.name,
    };
    parent.children.push(node);
  }

  return { roots, emptyLevels };
}

/**
 * As unidades da lista sem repetir a mesma unidade: o nó muitos-para-muitos aparece sob cada
 * pai (uma cópia por pai, ``itemKey`` igual) e só conta uma vez.
 */
export function uniqueOrgUnits(nodes: OrgTreeNode[]): OrgTreeNode[] {
  const seen = new Set<string>();
  return nodes.filter((node) => {
    const identity = `${node.levelCode}:${node.itemKey}`;
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
}

/**
 * Achata a árvore exibida numa lista plana de `RunForestNode`, no formato que
 * o `agents` exige para disparar o cadastro (`ForestNode` em
 * `services/agents/app/routers/config_migration.py`, consumido por
 * `build_plan` em `planner.py`).
 *
 * **A identidade de um nó no wire é o par `(item_type, item_key)`, e o pai é
 * o par `(parent_type, parent_key)`.** `planner.build_plan` indexa a floresta
 * por esse par e sobe a cadeia por ele — só a chave era ambígua: no ECC o
 * mesmo código numérico existe em vários níveis (7003 é company_code,
 * sales_organization e purchasing_organization ao mesmo tempo), e indexar só
 * por `item_key` fez o play do Company Code 7003 cadastrar a Purchasing
 * Organization 7003 no CBC real (2026-09-03).
 *
 * `item_key` é `itemKeyOf(node)` — composta como `parentId|id` para nós
 * não-raiz, ou só `id` para raiz — e `parent_key` é a chave COMPOSTA do pai,
 * passada pela recursão (não `node.parent.id`). A `key` de render inclui
 * level+parent+id só para desambiguar nós de chave composta na tela (ex.:
 * Storage Location), e o backend não conhece nem usa esse formato.
 *
 * `unit.children` fica sempre vazio: o formato é flat (uma entrada por nó,
 * ligada pelo par item_type/parent_key), não uma árvore aninhada — tensão de
 * design já registrada, não mexer.
 */
export function buildRunForest(roots: OrgTreeNode[]): RunForestNode[] {
  const flat: RunForestNode[] = [];

  // `parentItemKey` desce pela recursão em vez de sair de `node.parent`:
  // `OrgTreeNodeSummary` não carrega o `parentId` do próprio pai, e o
  // `parent_key` precisa ser a chave COMPOSTA do pai para casar com o
  // `item_key` dele no `build_plan` do backend.
  function walk(nodes: OrgTreeNode[], parentItemKey: string | null): void {
    for (const node of nodes) {
      // A cópia muitos-para-muitos é só exibição: a unidade vai ao disparo pela Sales Area.
      if (node.linkedCopy) continue;
      const itemKey = node.itemKey;
      flat.push({
        item_type: node.levelCode,
        item_key: itemKey,
        parent_key: node.parent ? parentItemKey : null,
        parent_type: node.parent?.levelCode ?? null,
        unit: {
          id: effectiveOrgId(node),
          source_id: node.id,
          // Sem nome extraído nem corrigido o nome segue vazio: o agente só usa o que vem do
          // banco, e o CBC diz se o campo é obrigatório.
          name: node.name ?? "",
          unit_type: node.levelCode,
          country: node.country,
          city: node.city,
          local_currency: node.localCurrency,
          subtype: node.subtype,
        },
      });
      walk(node.children, itemKey);
    }
  }

  walk(roots, null);
  return flat;
}

/** A entrada da unidade na floresta do wire, pelo par (tipo, chave). */
export function runForestEntryOf(
  forest: RunForestNode[],
  node: Pick<OrgTreeNode, "levelCode" | "itemKey">,
): RunForestNode | undefined {
  return forest.find((entry) => entry.item_type === node.levelCode && entry.item_key === node.itemKey);
}

/**
 * Os ancestrais da unidade na floresta do wire, da raiz até o pai, achados pelo par
 * (tipo, chave) do pai. O vínculo de pai vem da extração do ECC: um ciclo para a subida.
 */
export function ancestorsInRunForest(forest: RunForestNode[], unit: RunForestNode): RunForestNode[] {
  const chain: RunForestNode[] = [];
  const visited = new Set<RunForestNode>([unit]);
  let current = unit;
  for (;;) {
    const parent = forest.find(
      (entry) => entry.item_type === current.parent_type && entry.item_key === current.parent_key,
    );
    if (!parent || visited.has(parent)) return chain;
    chain.unshift(parent);
    visited.add(parent);
    current = parent;
  }
}

/**
 * A unidade dona da conversa que o ▶ religou (`openOrResume` devolve a conversa aberta
 * do nó, de qualquer unidade). Primeiro pelo último run do item: com o alias do D17 a
 * chave da conversa é a antiga e não casa com a da unidade. Sem item apontando para o
 * run, pela chave, só quando uma única unidade a tem: a conversa não diz o nível, e um
 * homônimo de outro nível não pode levar a conversa. `null` quando não se sabe de quem é.
 */
export function resumedConversationOwner(
  nodes: Iterable<OrgTreeNode>,
  opened: { runId: string; activityCode: string | null },
): OrgTreeNode | null {
  const byKey: OrgTreeNode[] = [];
  for (const node of nodes) {
    if (node.lastRunId === opened.runId) return node;
    if (opened.activityCode !== null && node.itemKey === opened.activityCode) byKey.push(node);
  }
  return byKey.length === 1 ? byKey[0] : null;
}

function collectOrgNodesByKey(nodes: OrgTreeNode[], into: Map<string, OrgTreeNode>): void {
  for (const node of nodes) {
    into.set(node.key, node);
    collectOrgNodesByKey(node.children, into);
  }
}

/**
 * Fecho de execução do play de um nó, calculado no cliente para decidir o que
 * mostrar no modal de confirmação antes de disparar — o fecho que vale no
 * disparo é o do backend (`build_plan`, planner.py). Esta função espelha o
 * algoritmo de lá, passo a passo:
 *
 * - sobe pela cadeia de `parent` até a raiz, ou até um ancestral **ausente**
 *   da floresta recebida (o `break` só existe por isso);
 * - **não interrompe a subida** ao encontrar um ancestral `completed`: um avô
 *   pendente sob um pai `completed` continua entrando no fecho;
 * - omite do resultado todo nó `completed` — inclusive o próprio `node`, se
 *   ele estiver `completed` (o play permanece clicável nesse estado por
 *   design, para permitir recadastro), o que pode devolver fecho vazio;
 * - `seen` protege de ciclo em `parent` (dado do ERP, não invariante nosso).
 *
 * `roots` precisa conter o `node` e sua cadeia de ancestrais — a floresta
 * filtrada exibida na tela serve, porque um ancestral só é removido pelo
 * filtro se nenhum descendente dele sobreviver (e o próprio `node` é um
 * descendente que sobreviveu).
 */
export function computeOrgRunClosure(node: OrgTreeNode, roots: OrgTreeNode[]): OrgTreeNode[] {
  const byKey = new Map<string, OrgTreeNode>();
  collectOrgNodesByKey(roots, byKey);
  return collectClosure(node, byKey, new Set<string>());
}

/**
 * Sobe a cadeia de um nó acumulando em `closure`. `visited` é compartilhado
 * entre chamadas do mesmo fecho de floresta: um ancestral comum a duas
 * unidades selecionadas entra uma única vez, e a subida para quando alcança
 * um trecho já percorrido.
 */
function collectClosure(
  node: OrgTreeNode,
  byKey: Map<string, OrgTreeNode>,
  visited: Set<string>,
): OrgTreeNode[] {
  const closure: OrgTreeNode[] = [];
  let current: OrgTreeNode | undefined = node;
  while (current !== undefined && !visited.has(current.key)) {
    visited.add(current.key);
    if (current.migrationStatus !== "completed") {
      closure.push(current);
    }
    const parentSummary = current.parent;
    if (parentSummary === null) break;
    current = byKey.get(parentSummary.key);
  }
  return closure;
}

/**
 * Fecho de execução do "Run all": a união dos fechos de cada nó de
 * `selectedRoots`, resolvida sobre `roots`.
 *
 * Os dois argumentos são florestas diferentes de propósito. `selectedRoots` é
 * o recorte de escopo — a árvore de exibição já filtrada por busca/tipo/país,
 * que define QUAIS unidades a pessoa selecionou. `roots` é a árvore canônica,
 * a mesma enviada ao backend, e é dela que saem os ancestrais: a projeção de
 * exibição consolida uma Company no cartão do seu Company Code homônimo, e
 * contar só os nós exibidos subestima o plano que o backend vai executar.
 */
export function computeOrgRunClosureForForest(
  selectedRoots: OrgTreeNode[],
  roots: OrgTreeNode[],
): OrgTreeNode[] {
  const byKey = new Map<string, OrgTreeNode>();
  collectOrgNodesByKey(roots, byKey);

  const selected = new Map<string, OrgTreeNode>();
  collectOrgNodesByKey(selectedRoots, selected);

  const visited = new Set<string>();
  const closure: OrgTreeNode[] = [];
  for (const node of selected.values()) {
    closure.push(...collectClosure(node, byKey, visited));
  }
  return closure;
}

/**
 * O nó e todos os seus descendentes, em pré-ordem.
 *
 * Existe porque o fecho de execução só SOBE (ancestrais), e o `build_plan` do
 * backend também — ele nunca desce. Para "cadastrar este nó e todos os filhos"
 * o escopo tem que sair daqui e ir no `selected_units`; o `trigger: "subtree"`
 * é apenas metadado gravado na linha do run.
 *
 * Não filtra por status de propósito: isto é o ESCOPO pedido pela pessoa. O
 * que já está `completed` é descartado pelo backend na montagem do plano.
 */
export function collectOrgSubtree(node: OrgTreeNode): OrgTreeNode[] {
  return collectSubtree(node, new Set<string>());
}

/**
 * `visited` existe pela mesma razão que o de `collectClosure`: o vínculo de
 * pai vem da extração do ECC, não é invariante nossa, e um `parent_id`
 * cíclico (A→B→A) faz a descida se repetir até estourar a pilha.
 */
function collectSubtree(node: OrgTreeNode, visited: Set<string>): OrgTreeNode[] {
  if (visited.has(node.key)) return [];
  visited.add(node.key);

  const subtree: OrgTreeNode[] = [node];
  for (const child of node.children) {
    subtree.push(...collectSubtree(child, visited));
  }
  return subtree;
}

export const INCOMPLETE_ORG_STATUSES = new Set([
  "structural_error",
  "not_found",
  "not_authorized",
  "error",
  "empty",
]);

export function isIncompleteOrgStatus(status: string): boolean {
  return INCOMPLETE_ORG_STATUSES.has(status);
}

function pruneNode(node: OrgTreeNode): OrgTreeNode | null {
  const children = node.children
    .map(pruneNode)
    .filter((child): child is OrgTreeNode => child !== null);

  // Só o vazio sem decisão some: a aprovada fica mesmo sem nome (o Sales Area, por
  // exemplo, não tem campo de nome no SAP) e entra na Execução e na contagem dela.
  if (
    node.nameStatus === "not_extracted"
    && node.name === null
    && children.length === 0
    && node.scopeVerdict !== "approved"
  ) {
    return null;
  }
  return { ...node, children };
}

/**
 * Um pai sem nome extraído só existe na árvore pra dar contexto a um filho
 * real (ex.: Plant sem nome mas com Storage Location extraída). Sem isso,
 * cascateia até sumir — visto no ECC real, a lista ficava gigante com nós
 * totalmente vazios.
 */
export function pruneEmptyOrgNodes(roots: OrgTreeNode[]): OrgTreeNode[] {
  return roots.map(pruneNode).filter((node): node is OrgTreeNode => node !== null);
}

function shouldPromoteCompanyRootForDisplay(node: OrgTreeNode): boolean {
  return (
    node.levelCode === "company" &&
    node.children.length > 0 &&
    node.children.every((child) => child.levelCode === "company_code")
  );
}

function matchingCompanyCodeForDisplay(company: OrgTreeNode): OrgTreeNode | undefined {
  return company.children.find((child) => company.id === `00${child.id}`);
}

function projectCompanyRootForDisplay(company: OrgTreeNode): OrgTreeNode[] {
  const matchingCompanyCode = matchingCompanyCodeForDisplay(company);

  if (matchingCompanyCode === undefined) {
    return company.children;
  }

  return [
    {
      ...matchingCompanyCode,
      children: [
        ...company.children.filter((child) => child.key !== matchingCompanyCode.key),
        ...matchingCompanyCode.children,
      ],
    },
  ];
}

/**
 * A árvore canônica mantém Company (6 dígitos) para preservar a relação real.
 * Quando o id da Company é exatamente o Company Code com dois zeros à frente,
 * a visualização consolida os dois no cartão do Company Code e transfere os
 * irmãos para ele. Assim, a UI não duplica a unidade e mantém o agrupamento
 * expandível; a relação canônica da Company continua disponível no drawer.
 */
export function projectOrgRootsForDisplay(roots: OrgTreeNode[]): OrgTreeNode[] {
  return roots.flatMap((node) =>
    shouldPromoteCompanyRootForDisplay(node) ? projectCompanyRootForDisplay(node) : [node],
  );
}

/** Algum nó da floresta (em qualquer profundidade) está neste status de cadastro? */
export function hasOrgNodeWithMigrationStatus(
  roots: OrgTreeNode[],
  status: MigrationStatus,
): boolean {
  return roots.some(
    (node) =>
      node.migrationStatus === status || hasOrgNodeWithMigrationStatus(node.children, status),
  );
}

export type OrgStatusBucket = "all" | "incomplete" | "no_parent" | "unknown_parent";

export interface OrgForestFilter {
  search?: string;
  levelCode?: string;
  unitTypeKeys?: OrgUnitTypeFilter[];
  countryCodes?: OrgCountryFilter[];
  statusBucket?: OrgStatusBucket;
  scopeVerdict?: ScopeVerdict;
  origin?: UnitOrigin;
  migrationStatus?: MigrationStatus;
}

function nodeMatchesOwnCriteria(node: OrgTreeNode, filter: OrgForestFilter): boolean {
  const requestedUnitTypeKeys =
    filter.unitTypeKeys && filter.unitTypeKeys.length > 0
      ? new Set(filter.unitTypeKeys)
      : filter.levelCode
        ? new Set([filter.levelCode])
        : null;

  if (requestedUnitTypeKeys !== null && !requestedUnitTypeKeys.has(node.unitTypeKey)) {
    return false;
  }
  const requestedCountryCodes =
    filter.countryCodes && filter.countryCodes.length > 0 ? new Set(filter.countryCodes) : null;
  if (requestedCountryCodes !== null) {
    const country = node.country?.trim();
    if (!country || !requestedCountryCodes.has(country)) {
      return false;
    }
  }
  if (filter.statusBucket && filter.statusBucket !== "all") {
    if (filter.statusBucket === "incomplete" && !isIncompleteOrgStatus(node.status)) {
      return false;
    }
    if (filter.statusBucket === "no_parent" && node.orphanReason !== "no_parent") {
      return false;
    }
    if (filter.statusBucket === "unknown_parent" && node.orphanReason !== "unknown_parent") {
      return false;
    }
  }
  if (filter.scopeVerdict && node.scopeVerdict !== filter.scopeVerdict) {
    return false;
  }
  if (filter.origin && node.origin !== filter.origin) {
    return false;
  }
  if (filter.migrationStatus && node.migrationStatus !== filter.migrationStatus) {
    return false;
  }
  const needle = filter.search?.trim().toLowerCase();
  if (needle) {
    const haystack = `${node.id} ${node.targetId ?? ""} ${node.name ?? ""}`.toLowerCase();
    if (!haystack.includes(needle)) {
      return false;
    }
  }
  return true;
}

function filterNode(node: OrgTreeNode, filter: OrgForestFilter): OrgTreeNode | null {
  const matchesOwnCriteria = nodeMatchesOwnCriteria(node, filter);
  const hasBranchPruningCriteria =
    Boolean(filter.levelCode) || Boolean(filter.unitTypeKeys && filter.unitTypeKeys.length > 0);

  const hasCountryCriteria = Boolean(filter.countryCodes && filter.countryCodes.length > 0);

  if (matchesOwnCriteria && !hasBranchPruningCriteria && !hasCountryCriteria) return node;

  const children = node.children
    .map((child) => filterNode(child, filter))
    .filter((child): child is OrgTreeNode => child !== null);
  if (matchesOwnCriteria) return { ...node, children };
  if (children.length === 0) return null;
  return { ...node, children };
}

/**
 * Um nó que só bate o filtro por causa de um descendente ainda precisa
 * aparecer — senão o descendente que casou fica órfão na tela. Um nó que
 * bate diretamente mantém a subárvore inteira (ex.: buscar "Plant SP"
 * também mostra as Storage Locations dela).
 */
export function filterOrgForest(roots: OrgTreeNode[], filter: OrgForestFilter): OrgTreeNode[] {
  const isNoop =
    !filter.search?.trim() &&
    !filter.levelCode &&
    !(filter.unitTypeKeys && filter.unitTypeKeys.length > 0) &&
    !(filter.countryCodes && filter.countryCodes.length > 0) &&
    (!filter.statusBucket || filter.statusBucket === "all") &&
    !filter.scopeVerdict &&
    !filter.origin &&
    !filter.migrationStatus;
  if (isNoop) {
    return roots;
  }
  return roots.map((node) => filterNode(node, filter)).filter((node): node is OrgTreeNode => node !== null);
}

export interface OrgStatusCounts {
  all: number;
  incomplete: number;
  no_parent: number;
  unknown_parent: number;
}

export interface OrgConformityCounts {
  conforming: number;
  non_conforming: number;
  not_evaluable: number;
}

export interface OrgNameStatusCounts {
  extracted: number;
  not_extracted: number;
}

export interface OrgRelationStatusCounts {
  assigned: number;
  no_parent: number;
  unknown_parent: number;
  many_to_many: number;
  not_applicable: number;
}

function walkCounts(nodes: OrgTreeNode[], counts: OrgStatusCounts): void {
  for (const node of nodes) {
    counts.all += 1;
    if (isIncompleteOrgStatus(node.status)) counts.incomplete += 1;
    if (node.orphanReason === "no_parent") counts.no_parent += 1;
    if (node.orphanReason === "unknown_parent") counts.unknown_parent += 1;
    walkCounts(node.children, counts);
  }
}

export function countOrgNodesByBucket(roots: OrgTreeNode[]): OrgStatusCounts {
  const counts: OrgStatusCounts = { all: 0, incomplete: 0, no_parent: 0, unknown_parent: 0 };
  walkCounts(roots, counts);
  return counts;
}

function walkNameStatusCounts(nodes: OrgTreeNode[], counts: OrgNameStatusCounts): void {
  for (const node of nodes) {
    counts[node.nameStatus] += 1;
    walkNameStatusCounts(node.children, counts);
  }
}

export function countOrgNodesByNameStatus(roots: OrgTreeNode[]): OrgNameStatusCounts {
  const counts: OrgNameStatusCounts = {
    extracted: 0,
    not_extracted: 0,
  };
  walkNameStatusCounts(roots, counts);
  return counts;
}

function walkRelationStatusCounts(nodes: OrgTreeNode[], counts: OrgRelationStatusCounts): void {
  for (const node of nodes) {
    counts[node.relationStatus] += 1;
    walkRelationStatusCounts(node.children, counts);
  }
}

export function countOrgNodesByRelationStatus(roots: OrgTreeNode[]): OrgRelationStatusCounts {
  const counts: OrgRelationStatusCounts = {
    assigned: 0,
    no_parent: 0,
    unknown_parent: 0,
    many_to_many: 0,
    not_applicable: 0,
  };
  walkRelationStatusCounts(roots, counts);
  return counts;
}

function walkConformityCounts(nodes: OrgTreeNode[], counts: OrgConformityCounts): void {
  for (const node of nodes) {
    counts[node.conformity.status] += 1;
    walkConformityCounts(node.children, counts);
  }
}

export function countOrgNodesByConformity(roots: OrgTreeNode[]): OrgConformityCounts {
  const counts: OrgConformityCounts = {
    conforming: 0,
    non_conforming: 0,
    not_evaluable: 0,
  };
  walkConformityCounts(roots, counts);
  return counts;
}

export interface OrgLevelOption {
  levelCode: string;
  levelName: string;
}

export interface OrgUnitTypeOption {
  value: string;
  label: string;
  count: number;
  source: "extracted";
}

export interface OrgCountryOption {
  value: string;
  label: string;
  count: number;
  source: "extracted";
}

function walkLevelOptions(nodes: OrgTreeNode[], seen: Map<string, string>): void {
  for (const node of nodes) {
    if (!seen.has(node.levelCode)) {
      seen.set(node.levelCode, node.levelName);
    }
    walkLevelOptions(node.children, seen);
  }
}

/** Alimenta o select de "Unit type" com os níveis que de fato têm dado — não com os 17 do cookbook inteiro. */
export function listOrgLevelOptions(roots: OrgTreeNode[]): OrgLevelOption[] {
  const seen = new Map<string, string>();
  walkLevelOptions(roots, seen);
  return Array.from(seen, ([levelCode, levelName]) => ({ levelCode, levelName }));
}

function walkUnitTypeOptions(nodes: OrgTreeNode[], seen: Map<string, OrgUnitTypeOption>): void {
  for (const node of nodes) {
    const existing = seen.get(node.unitTypeKey);
    if (existing) {
      existing.count += 1;
    } else {
      seen.set(node.unitTypeKey, {
        value: node.unitTypeKey,
        label: node.unitTypeLabel,
        count: 1,
        source: "extracted",
      });
    }
    walkUnitTypeOptions(node.children, seen);
  }
}

export function listOrgUnitTypeOptions(roots: OrgTreeNode[]): OrgUnitTypeOption[] {
  const seen = new Map<string, OrgUnitTypeOption>();
  walkUnitTypeOptions(roots, seen);
  return Array.from(seen.values());
}

function walkCountryOptions(nodes: OrgTreeNode[], seen: Map<string, OrgCountryOption>): void {
  for (const node of nodes) {
    const country = node.country?.trim();
    if (country) {
      const existing = seen.get(country);
      if (existing) {
        existing.count += 1;
      } else {
        seen.set(country, {
          value: country,
          label: country,
          count: 1,
          source: "extracted",
        });
      }
    }
    walkCountryOptions(node.children, seen);
  }
}

export function listOrgCountryOptions(roots: OrgTreeNode[]): OrgCountryOption[] {
  const seen = new Map<string, OrgCountryOption>();
  walkCountryOptions(roots, seen);
  return Array.from(seen.values());
}
